import { Close, Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { MdCloudUpload, MdHelpOutline } from "react-icons/md";
import integrationService from "../../../service/integrationService";
import { toast } from "react-toastify";
import errorHandler from "../../../utils/errorHandler";
import publicService from "../../../service/publicService";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import UnitOfMeasureSelect from "./UnitOfMeasureSelect";

const defaultValues = {
  businessName: "",
  entityId: "",
  file: null,
  taxes: [],
  priceUnits: [],
  hsnCode: "",
  isicCode: "",
  vat: { code: "ZERO_VAT", percent: 0, value: "Zero Value-Added Tax" },
};

const integrationSchema = Yup.object({
  priceUnits: Yup.array()
    .of(Yup.object({ code: Yup.string().required(), name: Yup.string().required() }))
    .min(1, "Select at least one unit of measurement")
    .required(),
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const EditConnectFIRS = ({ isOpen, onClose, refetch, currentStep, id, integration }) => {
  const [isMenu, setIsMenu] = React.useState({
    isEntityIDInfo: null,
    isUploadKeysInfo: null,
  });

  const { handleSubmit, formState, control, reset, watch } = useForm({
    mode: "all",
    resolver: yupResolver(integrationSchema),
    defaultValues: defaultValues,
  });

  const mutation = useMutation({
    mutationFn: (data) => integrationService.updateIntegration(data, { id: id, organizationId: integration?.organizationId }),
    onSuccess: (data) => {
      refetch();
      toast.success("Connection details updated successfully...");
      handleClose();
    },
    onError: ({ response }) => errorHandler(response),
  });

  const taxesQuery = useQuery({
    enabled: isOpen,
    queryKey: ["default-firs-taxes"],
    queryFn: () => publicService.getTaxes({}),
  });

  const hscodesQuery = useQuery({
    queryKey: ["hscodes"],
    queryFn: () => publicService.getHSCode({}),
  });

  const serviceCodesQuery = useQuery({
    queryKey: ["isiccodes"],
    queryFn: () => publicService.getServiceCodes({}),
  });

  function save(data) {
    const formData = new FormData();
    if (data.file) formData.append("file", data.file);
    formData.append("hsnCode", data.hsnCode ?? "");
    formData.append("isicCode", data.isicCode ?? "");

    let percent = 0;
    if (!isNaN(Number(data?.vat?.percent)) && typeof Number(data?.vat?.percent) === "number") {
      percent = Number(data?.vat?.percent);
    }

    formData.append(`vat[name]`, data?.vat?.code);
    formData.append(`vat[percentage]`, percent?.toString());

    data.taxes.forEach((item, idx) => {
      let percent = 0;
      if (!isNaN(Number(item?.percent)) && typeof Number(item?.percent) === "number") {
        percent = Number(item?.percent);
      }
      formData.append(`taxes[${idx}][name]`, item?.code);
      formData.append(`taxes[${idx}][percentage]`, percent?.toString());
    });

    data.priceUnits?.forEach((unit, idx) => formData.append(`priceUnits[${idx}]`, unit?.code));
    mutation.mutate(formData);
  }

  function handleChangeMenu(name, value) {
    setIsMenu((prev) => ({ ...prev, [name]: value }));
  }

  const errors = formState.errors;
  const vat = watch("vat");
  const taxes = watch("taxes");
  const taxData = taxesQuery?.data?.data?.data?.taxes ?? [];
  const vatData = taxesQuery?.data?.data?.data?.vat ?? [];
  const prevTaxes = integration?.data?.taxes?.map((item) => item?.name);
  const prevVAT = integration?.data?.vat;
  let hscodes = hscodesQuery?.data?.data?.data ?? [];
  let serviceCodes = serviceCodesQuery?.data?.data?.data ?? [];

  React.useEffect(() => {
    if (!integration) return;
    reset({
      hsnCode: integration?.data?.hsnCode ?? "",
      isicCode: integration?.data?.isicCode ?? "",
      entityId: integration?.data?.entityId ?? "",
      businessName: integration?.data?.businessName ?? "",
      taxes: taxData?.filter((item) => prevTaxes?.includes(item?.code)),
      priceUnits: integration?.data?.priceUnits ?? [],
      vat: vatData?.find((item) => prevVAT?.name === item?.code) ?? defaultValues.vat,
    });
  }, [id, isOpen, taxData?.length, vatData?.length]);

  function handleClose() {
    reset(defaultValues);
    onClose();
  }

  if (currentStep !== 0) return null;

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      maxWidth="sm"
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit(save),
        className: "w-100",
        style: { maxWidth: 550 },
      }}
    >
      <React.Fragment>
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Connect to FIRS</div>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
      </React.Fragment>

      <DialogContent>
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>Entity ID:</span>
            <IconButton size="small">
              <MdHelpOutline
                onClick={(e) => handleChangeMenu("isEntityIDInfo", e.currentTarget)}
                style={{ color: "#0F83EF", cursor: "pointer", fontSize: 20 }}
              />
            </IconButton>
          </label>
          <Controller
            name="entityId"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="text"
                  fullWidth
                  disabled
                  size="small"
                  variant="outlined"
                  value={field.value}
                  placeholder="Enter entity ID"
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["entityId"]?.message}
                  error={!!errors["entityId"]}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>Business Name:</label>
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["businessName"]}>
                <TextField
                  type="text"
                  fullWidth
                  disabled
                  size="small"
                  variant="outlined"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["businessName"]?.message}
                  error={!!errors["businessName"]}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>Upload Keys:</span>
            <IconButton size="small">
              <MdHelpOutline
                onClick={(e) => handleChangeMenu("isUploadKeysInfo", e.currentTarget)}
                style={{ color: "#0F83EF", cursor: "pointer", fontSize: 20 }}
              />
            </IconButton>
          </label>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="file"
                  fullWidth
                  size="small"
                  variant="outlined"
                  onChange={(e) => field.onChange(e.target.files[0])}
                  helperText={errors["file"]?.message}
                  error={!!errors["file"]}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <MdCloudUpload color="#1f53d7" size={26} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </FormControl>
            )}
          />
        </Box>

        <Box mb={3}>
          <label>
            HSN-Code:{" "}
          </label>
          <Controller
            name="hsnCode"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["hsnCode"]}>
                <Autocomplete
                  id="hsnCode-autocomplete"
                  options={hscodes}
                  getOptionLabel={(option) => `${option?.description} (${option?.hscode})` || ""}
                  value={hscodes.find((opt) => opt.hscode === field.value) || null}
                  onChange={(e, value) => field.onChange(value ? value.hscode : "")}
                  isOptionEqualToValue={(option, value) => option.hscode === value.hscode}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select HS Code" />}
                />
                {errors["hsnCode"] ? <FormHelperText>{errors["hsnCode"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            ISIC-Code:{" "}
          </label>
          <Controller
            name="isicCode"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["isicCode"]}>
                <Autocomplete
                  id="isicCode-autocomplete"
                  options={serviceCodes}
                  getOptionLabel={(option) => `${option?.description} (${option?.code})` || ""}
                  value={serviceCodes.find((opt) => opt.code === field.value) || null}
                  onChange={(e, value) => field.onChange(value ? value.code : "")}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select Service Code" />}
                />
                {errors["isicCode"] ? <FormHelperText>{errors["isicCode"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>

        <Box mb={3}>
          <label>
            Select VAT:{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </label>
          <Controller
            name="vat"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["vat"]}>
                <Autocomplete
                  id="tags-standard"
                  value={vat}
                  options={vatData}
                  defaultValue={defaultValues.vat}
                  getOptionLabel={(option) => option?.value}
                  onChange={(e, value) => field.onChange(value)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select VAT" />}
                />
                {errors["vat"] ? <FormHelperText className="">{errors["vat"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>Taxes:</span>
          </label>
          <Controller
            name="taxes"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  id="tags-standard"
                  options={taxData}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option?.value}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox icon={icon} color="secondary" checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option?.value}
                      </li>
                    );
                  }}
                  onChange={(e, value) => field.onChange(value)}
                  value={taxes}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select Taxes" />}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Units of Measurement:{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </label>
          <Controller
            name="priceUnits"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["priceUnits"]}>
                <UnitOfMeasureSelect
                  enabled={isOpen}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={!!errors["priceUnits"]}
                  helperText={
                    errors["priceUnits"]?.message ??
                    "The units this business trades in. NRS requires a unit of measurement on every invoice line, and they populate the dropdown in the invoice template."
                  }
                />
              </FormControl>
            )}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 2 }}>
        <Button variant="contained" color="secondary" type="submit" disabled={mutation.isPending} style={{ minWidth: 120 }}>
          {mutation.isPending ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
      <Popover
        open={Boolean(isMenu.isEntityIDInfo)}
        anchorEl={isMenu.isEntityIDInfo}
        onClose={() => handleChangeMenu("isEntityIDInfo", null)}
      >
        <Box p={3} bgcolor="#26293f" style={{ width: "400px" }}>
          <Typography mb={2} fontSize={18} fontWeight={500} color="#fff">
            Entity ID
          </Typography>
          <Typography mb={3} color="#fff">
            An Entity ID refers to a unique identification number assigned to a taxpayer (individual or organization) upon registration with
            FIRS. To get this, simply navigate to Account &gt; API Integration &gt; Entity ID.
          </Typography>

          <Box display="flex" justifyContent="end">
            <Button
              variant="contained"
              style={{ borderRadius: "50px", background: "#586AEB" }}
              onClick={() => handleChangeMenu("isEntityIDInfo", null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
      <Popover
        open={Boolean(isMenu.isUploadKeysInfo)}
        anchorEl={isMenu.isUploadKeysInfo}
        onClose={() => handleChangeMenu("isUploadKeysInfo", null)}
      >
        <Box p={3} bgcolor="#26293f" style={{ width: "400px" }}>
          <Typography mb={2} fontSize={18} fontWeight={500} color="#fff">
            Encryption Key
          </Typography>
          <Typography mb={3} color="#fff">
            This is a secure digital file that contain encryption keys or certificates used to digitally sign and authenticate electronic
            invoices. To get this file, simply navigate to Account &gt; API Integration &gt; Download keys. On your FIRS e-invoice dashboard
          </Typography>

          <Box display="flex" justifyContent="end">
            <Button
              variant="contained"
              style={{ borderRadius: "50px", background: "#586AEB" }}
              onClick={() => handleChangeMenu("isUploadKeysInfo", null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
    </Dialog>
  );
};

export default EditConnectFIRS;
